import { followSuit, trickWinner as winnerOf } from '../trick/rules.js';
import { type BridgeCard, sameCard } from './cards.js';
import { type BridgeContract, type BridgeTrump, buildContract } from './contract.js';
import { dealHands } from './deal.js';
import {
  type BridgeControllers,
  type BridgeSeat,
  type BridgeSide,
  SINGLE_PLAYER,
  leftOf,
  sideOf,
} from './seats.js';

export class IllegalBridgeMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalBridgeMoveError';
  }
}

export interface BridgeTrickPlay {
  readonly seat: BridgeSeat;
  readonly card: BridgeCard;
}

export interface BridgeCompletedTrick {
  readonly leader: BridgeSeat;
  readonly plays: readonly BridgeTrickPlay[];
  readonly winner: BridgeSeat;
}

/** `trump`: the declarer chooses trump. `play`: the 13 tricks. `done`: finished. */
export type BridgePhase = 'trump' | 'play' | 'done';

/**
 * The complete state of a game, INCLUDING all four hands. The engine and the server hold this; a
 * client or a robot must only ever read `viewFor(game, seat)` (view.ts), which is how robots cannot
 * cheat and how online play can send each player only what they may see.
 */
export interface BridgeGame {
  readonly seed: number;
  readonly dealer: BridgeSeat;
  readonly controllers: BridgeControllers;
  /** Cards each seat still holds. */
  readonly hands: Readonly<Record<BridgeSeat, readonly BridgeCard[]>>;
  readonly contract: BridgeContract;
  readonly phase: BridgePhase;
  /** The seat whose card is next (during `trump`, the seat that will make the opening lead). */
  readonly turn: BridgeSeat;
  /** The cards played to the current trick, in order. */
  readonly trick: readonly BridgeTrickPlay[];
  readonly tricks: readonly BridgeCompletedTrick[];
  readonly tricksWon: Readonly<Record<BridgeSide, number>>;
  /** True once the opening lead is made: dummy's hand is then shown to everyone. */
  readonly dummyExposed: boolean;
}

export interface NewBridgeGameOptions {
  readonly seed: number;
  readonly dealer?: BridgeSeat;
  /** Who controls each seat. Defaults to one human in the South seat. */
  readonly controllers?: BridgeControllers;
}

/** Deal a game. It starts in the `trump` phase: the declarer chooses trump next. */
export function newBridgeGame(opts: NewBridgeGameOptions): BridgeGame {
  const dealer = opts.dealer ?? 'N';
  const controllers = opts.controllers ?? SINGLE_PLAYER;
  const hands = dealHands(opts.seed, dealer);
  const contract = buildContract(hands, dealer, controllers);
  return {
    seed: opts.seed,
    dealer,
    controllers,
    hands,
    contract,
    phase: 'trump',
    turn: leftOf(contract.declarer),
    trick: [],
    tricks: [],
    tricksWon: { NS: 0, EW: 0 },
    dummyExposed: false,
  };
}

const TRUMPS: readonly BridgeTrump[] = ['C', 'D', 'H', 'S', 'NT'];

/** The declarer's choice of trump (or no trump). Moves the game into play. */
export function chooseTrump(game: BridgeGame, trump: BridgeTrump): BridgeGame {
  if (game.phase !== 'trump') throw new IllegalBridgeMoveError('trump is already chosen');
  if (!TRUMPS.includes(trump)) throw new IllegalBridgeMoveError(`not a trump choice: ${String(trump)}`);
  return { ...game, phase: 'play', contract: { ...game.contract, trump } };
}

/**
 * The seat that has to act next: the declarer chooses trump, and plays dummy's cards as well as their
 * own; every other seat plays its own. Null once the game is done.
 */
export function actingSeat(game: BridgeGame): BridgeSeat | null {
  if (game.phase === 'done') return null;
  if (game.phase === 'trump') return game.contract.declarer;
  return game.turn === game.contract.dummy ? game.contract.declarer : game.turn;
}

/** Cards the seat to play may legally play: follow suit if possible, otherwise anything; a leader may play any card. */
export function legalPlays(game: BridgeGame): BridgeCard[] {
  if (game.phase !== 'play') return [];
  return followSuit(game.hands[game.turn], game.trick[0]?.card.suit);
}

/** The seat that wins a completed trick: the highest trump if any was played, else the highest card of the suit led. */
export function trickWinner(plays: readonly BridgeTrickPlay[], trump: BridgeTrump): BridgeSeat {
  return winnerOf(plays, trump === 'NT' ? null : trump);
}

/** Play a card for the seat whose turn it is (the declarer plays dummy's cards). */
export function playCard(game: BridgeGame, card: BridgeCard): BridgeGame {
  if (game.phase !== 'play') throw new IllegalBridgeMoveError(`cannot play a card in the ${game.phase} phase`);
  if (!legalPlays(game).some((c) => sameCard(c, card))) {
    throw new IllegalBridgeMoveError(`${game.turn} cannot play that card now`);
  }
  const seat = game.turn;
  const hands = { ...game.hands, [seat]: game.hands[seat].filter((c) => !sameCard(c, card)) };
  const trick = [...game.trick, { seat, card }];
  const dummyExposed = true; // the first card played anywhere is the opening lead

  if (trick.length < 4) return { ...game, hands, trick, dummyExposed, turn: leftOf(seat) };

  const winner = trickWinner(trick, game.contract.trump as BridgeTrump);
  const tricks = [...game.tricks, { leader: (trick[0] as BridgeTrickPlay).seat, plays: trick, winner }];
  const tricksWon = { ...game.tricksWon, [sideOf(winner)]: game.tricksWon[sideOf(winner)] + 1 };
  return {
    ...game,
    hands,
    trick: [],
    tricks,
    tricksWon,
    dummyExposed,
    turn: winner,
    phase: tricks.length === 13 ? 'done' : 'play',
  };
}

export interface BridgeResult {
  readonly declaringSide: BridgeSide;
  readonly declarer: BridgeSeat;
  readonly trump: BridgeTrump;
  readonly tricksNeeded: number;
  readonly declarerTricks: number;
  readonly made: boolean;
  readonly overtricks: number;
  /** 0 if the contract failed; 1 for making it, plus 1 per overtrick, to a maximum of 3. */
  readonly stars: number;
}

export function bridgeResult(game: BridgeGame): BridgeResult | null {
  if (game.phase !== 'done') return null;
  const c = game.contract;
  const declarerTricks = game.tricksWon[c.declaringSide];
  const made = declarerTricks >= c.tricksNeeded;
  const overtricks = made ? declarerTricks - c.tricksNeeded : 0;
  return {
    declaringSide: c.declaringSide,
    declarer: c.declarer,
    trump: c.trump as BridgeTrump,
    tricksNeeded: c.tricksNeeded,
    declarerTricks,
    made,
    overtricks,
    stars: made ? Math.min(3, 1 + overtricks) : 0,
  };
}

